import { createElement, ReactNode, useEffect, useRef } from 'react';
import { DragSourceMonitor } from 'react-dnd';
import { useReactDndConstructs } from '../../../dnd/ReactDndContext';
import { Node } from '../';
import { useDesignTokens } from '../../../theme';
import { getDndStyleTokens } from '../../../style/reactDnd/DndStyleTokens';
import cx from 'classnames';
import cn from './Dnd.css.json';
import './Dnd.css';

export interface DraggableTreeNodeProps {
  id: string;
  children: ReactNode;
  dragType: string;
  dndPrefix: string;
  allowDrag?: boolean;
  node: Node;
  onDragStart?: () => void;
  onDragEnd?: (draggedItem: any, monitor: DragSourceMonitor) => void;
}

export const DraggableTreeNode = (props: DraggableTreeNodeProps) => {
  const dragNodeRef = useRef<HTMLDivElement | null>();
  const { useDrag, getEmptyImage } = useReactDndConstructs();
  const [{ isDragging }, drag, preview] = useDrag({
    // NOTE: type is now a required prop and is decoupled from item in react-dnd(v14.0.0)
    // @see - https://github.com/react-dnd/react-dnd/releases/tag/v14.0.0
    type: props.dragType,
    item: () => {
      // NOTE: begin() has been deprecated in react-dnd(v14.0.0)
      // replaced by functional version of item in react-dnd(v14.0.0)
      // @see - https://github.com/react-dnd/react-dnd/releases/tag/v14.0.0
      // @see - https://react-dnd.github.io/react-dnd/docs/api/use-drag
      props.onDragStart?.();
      return {
        id: props.id,
        type: props.dragType,
        node: props.node,
        dndPrefix: props.dndPrefix,
        width: dragNodeRef.current?.getBoundingClientRect().width,
      };
    },
    canDrag: props.allowDrag ?? false,
    collect: (monitor) => {
      return {
        isDragging: monitor.isDragging(),
      };
    },
    end: (draggedItem, monitor) => {
      const dropResult = monitor.getDropResult();
      if (dropResult && draggedItem) {
        props.onDragEnd?.(draggedItem, monitor);
      }
    },
  });

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview, getEmptyImage]);

  const dt = useDesignTokens();
  const dndTokens = getDndStyleTokens(dt);

  return (
    <div
      ref={(ele) => {
        drag(ele);
        dragNodeRef.current = ele;
      }}
      style={dndTokens}
      className={cx(props.allowDrag && cn.draggable, isDragging && cn.draggedItem)}
    >
      {props.children}
    </div>
  );
};
